import { useState, useRef } from 'react';
import api from 'services/api';
import { useTenant } from 'context/TenantContext';
import { exportPagePdf } from 'utils/exportPdf';
import ReactMarkdown from 'react-markdown';

// Rendu markdown — dupliqué du widget ChatbotIA (consigne : pas de module partagé).
const MD_COMPONENTS = {
  h1: ({ children }) => <div style={{ fontSize: 15, fontWeight: 700, color: '#8B0000', margin: '8px 0 4px' }}>{children}</div>,
  h2: ({ children }) => <div style={{ fontSize: 14, fontWeight: 700, color: '#8B0000', margin: '8px 0 4px' }}>{children}</div>,
  h3: ({ children }) => <div style={{ fontSize: 13, fontWeight: 700, color: '#8B0000', margin: '6px 0 3px' }}>{children}</div>,
  strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
  ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: 18 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: 18 }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: 2, lineHeight: 1.5 }}>{children}</li>,
  hr: () => <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '8px 0' }} />,
  p: ({ children }) => <p style={{ margin: '4px 0', lineHeight: 1.5 }}>{children}</p>,
  code: ({ children }) => <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3, fontSize: 11 }}>{children}</code>,
};

// Nom lisible du modele IA depuis l'ID renvoye par l'API (prefixes -> plus specifique d'abord).
// Fallback sur l'ID brut si non mappe : jamais un nom faux ni vide.
const MODEL_LABELS = [
  ['claude-haiku-4-5', 'Claude Haiku 4.5'],
  ['claude-sonnet-4-6', 'Claude Sonnet 4.6'],
  ['claude-sonnet-4', 'Claude Sonnet 4'],
  ['claude-opus-4', 'Claude Opus 4'],
];
function modelLabel(id) {
  if (!id) return '—';
  const hit = MODEL_LABELS.find(([prefix]) => id.startsWith(prefix));
  return hit ? hit[1] : id;
}

export default function RapportIAPage() {
  // Identite du TENANT pour l'en-tete/pied des exports PDF.
  // Sans raison sociale, NIF et RCCM, l'export est REFUSE (cf. utils/exportPdf.js).
  const tenant = useTenant();
  const [rapport, setRapport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const printRef = useRef();

  const generer = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.post('/ia/rapport-mensuel');
      setRapport(r.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la generation du rapport');
    } finally { setLoading(false); }
  };

  const d = rapport?.donnees;

  return (
    <div>
      <div className="stats-grid stats-grid--3">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Moteur IA</span></div>
          <div className="stat-card-value" style={{ fontSize: 16 }}>{modelLabel(rapport?.model)}</div>
          <div className="stat-card-sub">Anthropic</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Type</span></div>
          <div className="stat-card-value" style={{ fontSize: 16 }}>Rapport mensuel</div>
          <div className="stat-card-sub">Direction Generale</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Statut</span></div>
          <div className="stat-card-value" style={{ fontSize: 16, color: rapport ? '#2e7d32' : '#888' }}>
            {loading ? 'Generation...' : rapport ? 'Pret' : 'En attente'}
          </div>
        </div>
      </div>

      {!rapport && !loading && (
        <div className="content-card" style={{ textAlign: 'center', padding: '3rem' }}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="1.5" style={{ marginBottom: 16 }}>
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
          </svg>
          <h3 style={{ marginBottom: 8 }}>Rapport IA mensuel</h3>
          <p style={{ color: '#888', maxWidth: 500, margin: '0 auto 24px', fontSize: 13 }}>
            L'IA compile toutes les donnees du mois (agents, sites, incidents, finances) et genere un rapport complet avec KPIs, tendances et recommandations.
          </p>
          <button className="gss-btn gss-btn-primary" onClick={generer} style={{ padding: '12px 32px', fontSize: 14 }}>
            Generer le rapport IA
          </button>
          {error && <p style={{ color: '#DC2626', marginTop: 12 }}>{error}</p>}
        </div>
      )}

      {loading && (
        <div className="content-card" style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: 14, color: '#8B1A1A', fontWeight: 600, marginBottom: 8 }}>Generation du rapport en cours...</div>
          <p style={{ color: '#888', fontSize: 12 }}>L'IA analyse les donnees de la plateforme. Cela peut prendre 15 a 30 secondes.</p>
          <div style={{ width: 200, height: 4, background: '#eee', borderRadius: 2, margin: '16px auto', overflow: 'hidden' }}>
            <div style={{ width: '60%', height: '100%', background: 'linear-gradient(90deg, #8B1A1A, #C8A84E)', borderRadius: 2, animation: 'pulse 1.5s infinite' }} />
          </div>
        </div>
      )}

      {rapport && !loading && (
        <>
          {d && (
            <div className="stats-grid stats-grid--4" style={{ marginBottom: 16 }}>
              <div className="stat-card"><div className="stat-card-header"><span className="stat-card-label">Agents actifs</span></div><div className="stat-card-value">{d.effectifs?.actifs}</div></div>
              <div className="stat-card"><div className="stat-card-header"><span className="stat-card-label">CA mois</span></div><div className="stat-card-value" style={{ color: '#1565c0' }}>{Number(d.finances?.ca_mois || 0).toLocaleString()}</div><div className="stat-card-sub">FCFA</div></div>
              <div className="stat-card"><div className="stat-card-header"><span className="stat-card-label">Incidents</span></div><div className="stat-card-value" style={{ color: d.incidents?.critiques > 0 ? '#DC2626' : '#2e7d32' }}>{d.incidents?.total}</div><div className="stat-card-sub">{d.incidents?.critiques} critiques</div></div>
              <div className="stat-card"><div className="stat-card-header"><span className="stat-card-label">Impayes</span></div><div className="stat-card-value" style={{ color: '#D97706' }}>{Number(d.recouvrement?.montant_impaye || 0).toLocaleString()}</div><div className="stat-card-sub">{d.recouvrement?.factures_impayees} factures</div></div>
            </div>
          )}

          <div className="content-card" ref={printRef}>
            <div className="gss-toolbar">
              <div className="content-card-title">Rapport IA — {rapport.donnees?.periode || 'Mensuel'}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="gss-btn gss-btn-primary" onClick={() => exportPagePdf(`Rapport IA GSS-360 — ${rapport.donnees?.periode}`, printRef.current, tenant)}>Exporter PDF</button>
                <button className="gss-btn" onClick={generer}>Regenerer</button>
              </div>
            </div>
            <div style={{ padding: '1.5rem', fontSize: 13, lineHeight: 1.7 }}>
              <ReactMarkdown components={MD_COMPONENTS}>{rapport.rapport}</ReactMarkdown>
            </div>
            <div style={{ padding: '8px 16px', fontSize: 10, color: '#888', borderTop: '1px solid #eee', textAlign: 'right' }}>
              Genere par GSS-360 Intelligence IA le {new Date(rapport.genere_le).toLocaleString('fr-FR')}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
